// AI-CONTEXT: See docs/features/participation-value-dashboard-card.md

/**
 * @typedef {object} ParticipationSource
 * @property {string} id - source identifier
 * @property {string} title - human-readable title of the bron
 * @property {string} [publisher] - publishing organisation
 * @property {number} [year] - publication year
 * @property {string|null} [url] - link to the source, null when none is public
 * @property {string} [note] - extra remark (e.g. internal-only document)
 */

/**
 * @typedef {object} ParameterBlock
 * @property {string} paramKey - the parameters[] key this block reads
 * @property {string} label - Dutch label describing the figure
 * @property {'amount'|'amountPerHour'|'amountMap'|'rateMap'|'matrix'|'brackets'} kind - how the value should be rendered
 * @property {*} value - the parameter value (number, map, matrix or bracket list)
 * @property {string} [amountLabel] - kolomkop bij een amountMap (standaard 'Bedrag per jaar')
 * @property {Array<ParticipationSource>} sources - bronnen backing this figure
 */

/**
 * The parameter figure(s) behind each societal-value component, in display order.
 * Productiviteit is built from both the income table and the tax brackets; the
 * brackets carry no separate bron of their own.
 */
const COMPONENT_PARAM_BLOCKS = {
    incomePerson: [
        { paramKey: 'incomeByEducationAndHoursBand', label: 'Gemiddeld bruto inkomen per jaar', kind: 'matrix' },
        { paramKey: 'taxBrackets', label: 'Belastingschijven', kind: 'brackets' },
    ],
    volunteerValue: [{ paramKey: 'volunteerValuePerHour', label: 'Waarde per vrijwilligersuur', kind: 'amountPerHour' }],
    benefitSavings: [{ paramKey: 'benefitAmountPerYear', label: 'Uitkeringsbedrag per jaar', kind: 'amountMap' }],
    publicCostSavings: [
        { paramKey: 'bijstandUitvoeringskostenPerYear', label: 'Uitvoeringskosten bijstand per jaar', kind: 'amount' },
    ],

    // Parameterset 2026.3: de onderdelen zijn de posten die in het totaal
    // tellen (buildSocietalValueBreakdown), op hun naam uit `parameters.posts`.
    'Bruto loon': [
        { paramKey: 'incomeByEducationAndHoursBand', label: 'Bruto loon per jaar (mediaan uurloon × uren × 52)', kind: 'matrix' },
    ],
    'Verwachte loonwinst na opleiding': [
        {
            paramKey: 'wageGainFromEducation',
            label: 'Slaagkans per opleidingsniveau',
            kind: 'rateMap',
            select: (value) => value.successRate,
        },
    ],
    'Opgegeven vrije tijd': [
        {
            paramKey: 'freeTime',
            label: 'Deel van de netto compensatie dat vrije tijd is',
            kind: 'rateMap',
            select: (value) => ({ 'Wie gaat werken': value.factorEntrant, 'Wie meer uren gaat werken': value.factorExtraHours }),
        },
    ],
    'Uitvoeringskosten bijstand': [
        { paramKey: 'bijstandUitvoeringskostenPerYear', label: 'Uitvoeringskosten bijstand per jaar', kind: 'amount' },
    ],
    Dagbesteding: [
        {
            paramKey: 'dagbestedingPerDagdeel',
            label: 'Dagbesteding per dagdeel',
            kind: 'amountMap',
            amountLabel: 'Bedrag per dagdeel',
            select: (value) => Object.fromEntries(Object.entries(value).map(([wet, { amount }]) => [wet, amount])),
        },
    ],
    Vrijwilligerswerk: [{ paramKey: 'volunteerValuePerHour', label: 'Waarde per vrijwilligersuur', kind: 'amountPerHour' }],
}

/**
 * A sourceRefs entry is either a flat array of source IDs or a nested object
 * whose values are arrays of IDs (e.g. benefitAmountPerYear, keyed per
 * uitkeringstype). Flatten either shape to a flat list of IDs.
 * @param {string[]|Object<string, *>|undefined} ref
 * @returns {string[]}
 */
const collectIds = (ref) => {
    if (!ref) return []
    if (Array.isArray(ref)) return ref
    if (typeof ref === 'object') return Object.values(ref).flatMap(collectIds)
    return []
}

/**
 * Builds the figures-and-bronnen blocks behind one societal-value component:
 * each parameter the component is computed from, its raw value, and the bronnen
 * that back it (resolved via `parameters.sourceRefs` + `parameters.bronnen`).
 * Blocks whose parameter value is absent are skipped, so an incomplete payload
 * never renders an empty figure.
 *
 * @param {string} componentKey - incomePerson | volunteerValue | benefitSavings | publicCostSavings (2026.1/2026.2), of een postnaam (2026.3)
 * @param {{ sourceRefs?: object, bronnen?: Object<string, ParticipationSource> }} [parameters]
 * @returns {Array<ParameterBlock>}
 */
const getComponentParameterBlocks = (componentKey, parameters) => {
    const params = parameters || {}
    const refs = params.sourceRefs || {}
    const bronnen = params.bronnen || {}
    const defs = COMPONENT_PARAM_BLOCKS[componentKey] || []

    return defs
        .map(({ paramKey, label, kind, select, amountLabel }) => {
            const raw = params[paramKey]
            if (raw === undefined || raw === null) return null

            const ids = [...new Set(collectIds(refs[paramKey]))]
            const sources = ids.map((id) => bronnen[id]).filter(Boolean)
            const value = select ? select(raw) : raw

            return { paramKey, label, kind, value, sources, ...(amountLabel ? { amountLabel } : {}) }
        })
        .filter(Boolean)
}

export default getComponentParameterBlocks
